import clsx from 'clsx';
import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import Spinner from '@components/Spinner';
import useAnchorLinkTracking from '@hooks/useAnchorLinkTracking';
import useEventTracking from '@hooks/useEventTracking';
import useRecommendedProjects from '@hooks/useRecommendedProjects';
import { pro_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { impression } from '@tracking';
import { capitalizeTopic } from '@utils/formatting';

import ProjectCards from './ProjectCards';
import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS, VIEW_ALL } from './constants';
import useRegisterSection from './useRegisterSection';

interface ForYouDataToolsProps {
  mostReadTopic?: string;
}

const ForYouDataTools = ({ mostReadTopic }: ForYouDataToolsProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.DataTools,
  });
  const { projects, isLoadingProjects } = useRecommendedProjects(mostReadTopic);
  const projectCardsRef = useAnchorLinkTracking({
    enabled: !!projects,
    textSelector: 'h3',
    trackingLocation: TrackingLocation.DataTools,
  });

  const dataToolsDisplayText = useMemo(() => {
    if (!projects) return FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.Pro.DataTools;

    const engaged = FOR_YOU_ENGAGEMENT_LABELS.Engaged.Pro.DataTools;
    const label = mostReadTopic
      ? `Related to ${capitalizeTopic(mostReadTopic)} Coverage`
      : engaged.label;

    return { ...engaged, label };
  }, [projects, mostReadTopic]);

  useRegisterSection(FOR_YOU_SECTION_IDS.DataTools, isLoadingProjects || !!projects);

  if (!isLoadingProjects && (!projects || projects.length === 0)) return null;

  const handleViewAllClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.AnchorLink, clickText: VIEW_ALL }));
  };

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.DataTools}
      highlight={dataToolsDisplayText.label}
      subtitle={dataToolsDisplayText.subtitle}
      title={dataToolsDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView && trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.DataTools }));
          }}
          triggerOnce
        >
          {!isLoadingProjects && projects ? (
            <div className="mt-32 flex flex-col gap-32">
              <div
                className={clsx(
                  '-mx-16 flex flex-row flex-nowrap justify-start gap-32 overflow-x-auto px-16 sm:mx-0 sm:justify-center sm:px-0 md:grid md:gap-64',
                  {
                    'md:grid-cols-2': projects.length <= 2,
                    'md:grid-cols-3': projects.length === 3,
                  }
                )}
                ref={projectCardsRef}
              >
                <ProjectCards projects={projects} />
              </div>
              <a
                className="text-primary-black transition-colors duration-200 hover:text-primary-magenta dark:hover:text-primary-pink"
                href={pro_path()}
                onClickCapture={handleViewAllClick}
              >
                {VIEW_ALL}
              </a>
            </div>
          ) : (
            <div className="grid w-full place-items-center">
              <Spinner marginY="small" />
            </div>
          )}
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouDataTools;
